/**
 * useInactivityTimeout — Déconnexion automatique après inactivité
 *
 * Détecte l'activité utilisateur (clic, mouvement souris, scroll, clavier)
 * et persiste un timestamp `skyapp:lastActivity` (localStorage) qui survit
 * à la fermeture du navigateur. Au montage, à la reprise de focus/visibilité
 * (retour d'onglet en arrière-plan, réveil de veille) et à l'ouverture,
 * le temps réellement écoulé depuis cette dernière activité est recalculé —
 * un simple setTimeout ne suffit pas car il ne survit ni à la fermeture du
 * navigateur ni fiablement à la veille du poste.
 *
 * Ne casse pas le refresh token Supabase — le timer est indépendant des
 * appels réseau, il se base uniquement sur l'activité UI réelle (les
 * rafraîchissements techniques de token/API ne réinitialisent PAS l'activité).
 *
 * Usage :
 *   useInactivityTimeout({
 *     isActive: !!user,             // activer seulement si connecté
 *     userId: user?.id,             // namespace du timestamp persistant
 *     timeoutMs: SESSION_IDLE_TIMEOUT_MS, // défaut 10 minutes
 *     onTimeout: () => { ... }      // callback de logout
 *   });
 */

import { useEffect, useRef } from 'react';
import { recordUserActivity, getLastActivity, SESSION_IDLE_TIMEOUT_MS } from '../auth/authSync';

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'click', 'keydown', 'scroll', 'touchstart'];
// Throttle d'écriture localStorage pendant une activité continue (évite des écritures excessives).
const ACTIVITY_WRITE_THROTTLE_MS = 8 * 1000;

/**
 * @param {object}   options
 * @param {boolean}  options.isActive   - Activer le timer (false = hook inactif, ex. avant login)
 * @param {string}   [options.userId]   - Id de l'utilisateur courant (namespace du timestamp persistant)
 * @param {number}   [options.timeoutMs=SESSION_IDLE_TIMEOUT_MS] - Durée d'inactivité avant logout (ms)
 * @param {Function} options.onTimeout  - Callback appelé quand le timeout est atteint
 */
export function useInactivityTimeout({ isActive, userId, timeoutMs = SESSION_IDLE_TIMEOUT_MS, onTimeout }) {
  const timerRef = useRef(null);
  const lastWriteRef = useRef(0);
  // Ref stable vers le callback pour éviter de recréer les listeners
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  useEffect(() => {
    // Si l'utilisateur n'est pas connecté (ou id inconnu), ne rien faire
    if (!isActive || !userId) return;

    const armTimer = (ms) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        onTimeoutRef.current?.();
      }, ms);
    };

    // Relit le timestamp persistant et programme soit un logout immédiat
    // (temps déjà dépassé, ex. reprise après fermeture navigateur/veille),
    // soit un timer pour le temps restant exact.
    const checkPersistedAndArm = () => {
      const last = getLastActivity(userId);
      if (last == null) {
        // Aucune trace fiable (première connexion, ou migration sans historique) :
        // on initialise une fenêtre complète plutôt que de forcer un logout.
        recordUserActivity(userId);
        armTimer(timeoutMs);
        return;
      }
      const elapsed = Date.now() - last;
      if (elapsed >= timeoutMs) {
        onTimeoutRef.current?.();
      } else {
        armTimer(timeoutMs - elapsed);
      }
    };

    const onUserActivity = () => {
      const now = Date.now();
      if (now - lastWriteRef.current >= ACTIVITY_WRITE_THROTTLE_MS) {
        lastWriteRef.current = now;
        recordUserActivity(userId);
      }
      armTimer(timeoutMs);
    };

    // Retour d'onglet en arrière-plan / réveil de veille : le setTimeout peut
    // avoir été gelé par le navigateur, on revalide toujours via le timestamp.
    const onVisibleOrFocus = () => {
      if (document.visibilityState && document.visibilityState !== 'visible') return;
      checkPersistedAndArm();
    };

    // Vérification initiale (montage = reprise après fermeture/veille éventuelle)
    checkPersistedAndArm();

    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, onUserActivity, { passive: true });
    });
    window.addEventListener('focus', onVisibleOrFocus);
    window.addEventListener('pageshow', onVisibleOrFocus);
    document.addEventListener('visibilitychange', onVisibleOrFocus);

    return () => {
      // Nettoyage : supprimer les listeners et le timer
      if (timerRef.current) clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, onUserActivity);
      });
      window.removeEventListener('focus', onVisibleOrFocus);
      window.removeEventListener('pageshow', onVisibleOrFocus);
      document.removeEventListener('visibilitychange', onVisibleOrFocus);
    };
  }, [isActive, userId, timeoutMs]); // Re-créer si isActive, userId ou timeout change
}
